import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLinkedinIn, FaGithub, FaEnvelope, FaPhone, FaMapMarkerAlt, FaInstagram } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { personalInfo, navigation } from '../../data/profileData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'LinkedIn',
      icon: <FaLinkedinIn />,
      href: personalInfo.linkedin,
    },
    {
      name: 'GitHub',
      icon: <FaGithub />,
      href: personalInfo.github,
    },
    {
      name: 'X (Twitter)',
      icon: <FaXTwitter />,
      href: 'https://x.com/rakeshkuma8912',
    },
    {
      name: 'Instagram',
      icon: <FaInstagram />,
      href: 'https://www.instagram.com/tripathirakeshkumar',
    },
    {
      name: 'Email',
      icon: <FaEnvelope />,
      href: `mailto:${personalInfo.email}`,
    },
  ];

  const quickLinks = navigation.filter(item =>
    ['Home', 'About', 'Experience', 'Skills', 'Contact'].includes(item.name)
  );

  const services = [
    'IT Consulting',
    'ERPNext Implementation',
    'Cybersecurity',
    'Corporate Training',
    'Cloud Solutions',
  ];

  return (
    <footer className="bg-dark-800 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-xl">
                RK
              </div>
              <span className="font-heading font-bold text-xl">
                {personalInfo.firstName} {personalInfo.lastName}
              </span>
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              IT Professional with 25+ years of experience in technology leadership,
              specializing in Linux, ERPNext, Cybersecurity, and Cloud Solutions.
            </p>
            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-dark-700 flex items-center justify-center text-gray-400 hover:bg-primary-500 hover:text-white transition-all duration-300"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={social.name}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary-400 transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-gray-400">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <FaEnvelope className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="text-gray-400 hover:text-primary-400 transition-colors duration-200 break-all"
                >
                  {personalInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <FaPhone className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-400">{personalInfo.phone}</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaMapMarkerAlt className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-400">{personalInfo.location}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              &copy; {currentYear} {personalInfo.name}. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-primary-400 transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-primary-400 transition-colors duration-200"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
